import { type AccountType, formatEUR } from '@budget/shared';
import { useState } from 'react';
import { useAccounts, useCreateAccount, useReconcileAccount } from '../api/hooks';
import { AmountField } from '../components/AmountField';
import { BottomSheet } from '../components/BottomSheet';
import { GlyphPicker } from '../components/GlyphPicker';
import { accountGlyph } from '../components/glyphs';
import { useCreationPanel } from '../components/useCreationPanel';

function localToday(): string {
  const now = new Date();
  return `${now.getFullYear()}-${String(now.getMonth() + 1).padStart(2, '0')}-${String(now.getDate()).padStart(2, '0')}`;
}

export function AccountsScreen() {
  const accounts = useAccounts();
  const create = useCreateAccount();
  const reconcile = useReconcileAccount();

  const [open, setOpen] = useCreationPanel();
  const [name, setName] = useState('');
  const [type, setType] = useState<AccountType>('checking');
  const [balanceCents, setBalanceCents] = useState(0);
  const [icon, setIcon] = useState('');

  const [reconciling, setReconciling] = useState<string | null>(null);
  const [targetCents, setTargetCents] = useState(0);
  const [lastAdjustment, setLastAdjustment] = useState<number | null>(null);

  async function applyReconcile(accountId: string) {
    const result = await reconcile.mutateAsync({ accountId, balanceCents: targetCents });
    setLastAdjustment(result.adjustmentCents);
  }

  async function submit() {
    if (name.trim() === '') return;
    await create.mutateAsync({
      name: name.trim(),
      type,
      initialBalanceCents: balanceCents,
      openedOn: localToday(),
      icon,
    });
    setName('');
    setBalanceCents(0);
    setIcon('');
    setOpen(false);
  }

  return (
    <div className="px-5 pt-4">
      <h1 className="text-xl font-semibold">Comptes</h1>

      <ul className="mt-4 overflow-hidden rounded-2xl border border-line bg-surface">
        {(accounts.data ?? []).map((account) => (
          <li key={account.id} className="border-b border-line last:border-b-0">
            <div className="flex items-center justify-between gap-3 px-4 py-3">
              <span aria-hidden="true" className="text-xl">
                {accountGlyph(account)}
              </span>
              <div className="min-w-0 flex-1">
                <p className="truncate text-sm">{account.name}</p>
                <p className="text-xs text-ink-muted">
                  {account.type === 'checking' ? 'Compte courant' : 'Épargne'}
                </p>
              </div>
              <span className="tabular text-sm font-medium">{formatEUR(account.balanceCents)}</span>
              <button
                type="button"
                onClick={() => {
                  setReconciling(reconciling === account.id ? null : account.id);
                  setTargetCents(account.balanceCents);
                  setLastAdjustment(null);
                }}
                className="rounded-lg border border-line px-2 py-1 text-xs text-ink-secondary"
              >
                Recaler
              </button>
            </div>

            {reconciling === account.id && (
              <div className="border-t border-line bg-plane px-4 py-4">
                <AmountField
                  label="Solde affiché par la banque"
                  valueCents={targetCents}
                  onChange={setTargetCents}
                  hint="L’écart sera écrit comme un ajustement daté d’aujourd’hui."
                />
                {lastAdjustment !== null && (
                  <p className="mt-2 text-sm" role="status">
                    {lastAdjustment === 0
                      ? 'Le solde était déjà juste, aucun ajustement.'
                      : `Ajustement de ${formatEUR(lastAdjustment)} enregistré.`}
                  </p>
                )}
                <div className="mt-3 flex gap-2">
                  <button
                    type="button"
                    onClick={() => setReconciling(null)}
                    className="flex-1 rounded-xl border border-line py-3 text-sm"
                  >
                    Fermer
                  </button>
                  <button
                    type="button"
                    disabled={reconcile.isPending}
                    onClick={() => void applyReconcile(account.id)}
                    className="flex-1 rounded-xl bg-series py-3 text-sm font-medium text-white disabled:opacity-50"
                  >
                    Recaler
                  </button>
                </div>
              </div>
            )}
          </li>
        ))}
        {(accounts.data ?? []).length === 0 && (
          <li className="px-4 py-4 text-sm text-ink-secondary">
            Commencez par votre compte courant : c’est lui qui porte la courbe.
          </li>
        )}
      </ul>

      {open && (
        <BottomSheet label="Nouveau compte" onClose={() => setOpen(false)}>
          <div className="mt-4 flex flex-col gap-4">
            <label className="flex flex-col gap-1.5">
              <span className="text-sm text-ink-secondary">Nom</span>
              <input
                type="text"
                value={name}
                onChange={(event) => setName(event.target.value)}
                placeholder="Compte courant"
                className="rounded-xl border border-line bg-plane px-4 py-3 text-base outline-none focus:border-series"
              />
            </label>

            <div className="flex rounded-lg border border-line p-0.5 text-sm">
              {(['checking', 'savings'] as const).map((option) => (
                <button
                  key={option}
                  type="button"
                  onClick={() => setType(option)}
                  className={`flex-1 rounded-md px-3 py-2 ${
                    type === option ? 'bg-series text-white' : 'text-ink-secondary'
                  }`}
                >
                  {option === 'checking' ? 'Courant' : 'Épargne'}
                </button>
              ))}
            </div>

            <GlyphPicker
              label="Pictogramme"
              value={icon}
              onChange={setIcon}
              fallbackGlyph={accountGlyph({ type })}
            />

            <AmountField
              label="Solde actuel"
              valueCents={balanceCents}
              onChange={setBalanceCents}
              hint="Le point de départ : tout le reste se déduit des écritures."
            />

            <div className="flex gap-2">
              <button
                type="button"
                onClick={() => setOpen(false)}
                className="flex-1 rounded-xl border border-line py-3 text-sm"
              >
                Annuler
              </button>
              <button
                type="button"
                disabled={create.isPending}
                onClick={() => void submit()}
                className="flex-1 rounded-xl bg-series py-3 text-sm font-medium text-white disabled:opacity-50"
              >
                Créer
              </button>
            </div>
          </div>
        </BottomSheet>
      )}
    </div>
  );
}
